<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { QuillEditor } from '@vueup/vue-quill'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import ModalVariantConfirmation from '@/components/modal/modal-variant-confirmation.vue'
import PageContent from '@/components/page-content.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import useLoader from '@/composables/loader'
import ButtonPrimary from '@/components/button-primary.vue'
import ButtonHighlighted from '@/components/button-highlighted.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import {
  faFloppyDisk,
  faHandPointUp,
  faXmark,
  faArrowLeft
} from '@fortawesome/pro-regular-svg-icons'
import CreditNoteSkeleton from '@/features/credit-notes/components/CreditNoteSkeleton.vue'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import CreditNoteSkeletonCustomerDetails from '@/features/credit-notes/components/CreditNoteSkeletonCustomerDetails.vue'
import CreditNoteSkeletonBillTotal from '@/features/credit-notes/components/CreditNoteBillTotal.vue'
import CreditNoteSkeletonDetails from '@/features/credit-notes/components/CreditNoteSkeletonDetails.vue'
import Card from '@/components/Card/card-container.vue'
import CardContent from '@/components/Card/card-content.vue'
import CardHeader from '@/components/Card/card-header.vue'
import CreditNoteLines from '@/features/credit-notes/components/CreditNoteLines.vue'
import { CreditNoteStatus } from '@/features/credit-notes/constants'
import { calcCreditNoteTotals } from '@/features/credit-notes/utils/totals'
import {
  useGetCreditNote,
  useUpdateCreditNoteLines,
  useDeleteCreditNoteLine,
  useApproveCreditNote,
  useSendCreditNote
} from '@/composables/billing/credit-notes'
import PageTitle from '@/components/page-title.vue'
import useNotifications from '@/composables/notifications'
import {
  getAssetDetailsForCreditNote,
  getCustomerForCreditNote,
  getCreditNoteById
} from '@/api/billing/credit-notes'
import type { CreditNoteCharge, CreateCreditNoteResponse } from '@/api/billing/credit-note-schemas'

defineOptions({ name: 'EditCreditNote' })

const route = useRoute()
const router = useRouter()
const { addSuccess, addError } = useNotifications()
const { wrap } = useLoader()

const creditNoteId = computed(() => {
  const n = Number(route.params.id)
  return Number.isFinite(n) ? n : 0
})

// Credit-note GET doesn't return assetCode → keep it in querystring so we can re-query details.
const assetCode = computed(() => String(route.query.assetCode || '').trim())
const hasAssetCode = computed(() => assetCode.value.length > 0)

const assetQuery = useQuery({
  queryKey: computed(() => ['credit-note', 'asset', assetCode.value]),
  enabled: hasAssetCode,
  queryFn: () => getAssetDetailsForCreditNote(assetCode.value),
  placeholderData: prev => prev
})

const customerQuery = useQuery({
  queryKey: computed(() => {
    const a = assetQuery.data.value
    return [
      'credit-note',
      'customer',
      a?.customerExternalIdentifier ?? null,
      a?.contract?.contractFundEntity?.id ?? null
    ]
  }),
  enabled: computed(() => !!assetQuery.data.value),
  queryFn: () => {
    const a = assetQuery.data.value!
    return getCustomerForCreditNote({
      customerExternalIdentifier: a.customerExternalIdentifier,
      contractFundEntityId: a.contract.contractFundEntity.id
    })
  },
  placeholderData: prev => prev
})

const {
  data: note,
  isLoading: creditNoteLoading,
  isError: creditNoteIsError
} = useGetCreditNote(creditNoteId)
const { mutateAsync: updateLines, isPending: isSaving } = useUpdateCreditNoteLines(creditNoteId)
const { mutateAsync: deleteLine, isPending: isDeletingLine } = useDeleteCreditNoteLine(creditNoteId)
const { mutateAsync: approve, isPending: isApproving } = useApproveCreditNote()
const { mutateAsync: send } = useSendCreditNote()

type Line = { id?: number; description: string; kWh: number; tariffPerKWh: number }
const lines = ref<Line[]>([])
const vatPercentage = ref(0)
const notes = ref('')
const serverItemIds = ref<Set<number>>(new Set())
const isInitialised = ref(false)
const showLeaveConfirm = ref(false)
const leaveTo = ref<any>(null)
const allowLeave = ref(false)

function confirmLeave() {
  allowLeave.value = true
  showLeaveConfirm.value = false
  const to = leaveTo.value
  leaveTo.value = null
  if (to) router.push(to)
}

function rejectLeave() {
  showLeaveConfirm.value = false
  leaveTo.value = null
}

onBeforeRouteLeave(to => {
  if (allowLeave.value) return true
  if (!hasUnsavedChanges.value) return true

  leaveTo.value = to
  showLeaveConfirm.value = true
  return false
})

function onBeforeUnload(e: BeforeUnloadEvent) {
  if (!hasUnsavedChanges.value) return
  e.preventDefault()
  e.returnValue = ''
}

onMounted(() => window.addEventListener('beforeunload', onBeforeUnload))
onUnmounted(() => window.removeEventListener('beforeunload', onBeforeUnload))

function hydrateFromNote(n: CreateCreditNoteResponse) {
  serverItemIds.value = new Set((n.creditNoteItems ?? []).map(it => it.id))

  vatPercentage.value = normaliseVat(n.vatPercentage)
  notes.value = n.notes ?? ''

  const src = n.creditNoteItems?.length
    ? n.creditNoteItems.map(it => ({
        id: it.id,
        description: it.description ?? '',
        kWh: it.quantityInKWh ?? 0,
        tariffPerKWh: (it.tariffInCents ?? 0) / 100
      }))
    : [{ description: '', kWh: 0, tariffPerKWh: 0 }]

  lines.value = src
}

watch(
  note,
  (n: CreateCreditNoteResponse | undefined) => {
    if (!n) return
    if (!isInitialised.value) {
      hydrateFromNote(n)
      isInitialised.value = true
      return
    }
    if (!hasUnsavedChanges.value) {
      hydrateFromNote(n)
    }
  },
  { immediate: true }
)

function goBackToDashboard() {
  router.push({ name: 'invoices.list' })
}

function normaliseVat(value: unknown): number {
  if (typeof value !== 'number') return 0
  if (Number.isNaN(value)) return 0
  if (value <= 0 || value > 100) return 0
  return value
}

function normaliseNotes(value: unknown): string {
  const s = String(value ?? '').trim()
  return s === '<p><br></p>' ? '' : s
}

function lineSignature(l: {
  id?: number
  description?: string
  kWh?: number
  tariffPerKWh?: number
}) {
  const idPart = l.id != null ? `id:${l.id}` : 'id:none'
  const desc = (l.description ?? '').trim()
  const kwh = Number(l.kWh ?? 0)
  const tariff = Number(l.tariffPerKWh ?? 0)
  return `${idPart}|d:${desc}|k:${kwh}|t:${tariff}`
}

watch(
  note,
  (n: CreateCreditNoteResponse | undefined) => {
    if (!n) return
    vatPercentage.value = normaliseVat(n.vatPercentage)
    notes.value = n.notes ?? ''

    const src = n.creditNoteItems?.length
      ? n.creditNoteItems.map(it => ({
          id: it.id,
          description: it.description ?? '',
          kWh: it.quantityInKWh ?? 0,
          tariffPerKWh: (it.tariffInCents ?? 0) / 100
        }))
      : [{ description: '', kWh: 0, tariffPerKWh: 0 }]

    lines.value = src
  },
  { immediate: true }
)

const statusUi = computed<CreditNoteStatus>(() => {
  const s = String(note.value?.status ?? '')
  switch (s) {
    case CreditNoteStatus.Approved:
      return CreditNoteStatus.Approved
    case CreditNoteStatus.Sent:
      return CreditNoteStatus.Sent
    case CreditNoteStatus.Cancelled:
      return CreditNoteStatus.Cancelled
    default:
      // backend sends "ActionRequired" etc -> treat as Draft in UI
      return CreditNoteStatus.Draft
  }
})

const hasUnsavedChanges = computed(() => {
  const n = note.value
  if (!n) return false
  if (normaliseVat(n.vatPercentage) !== normaliseVat(vatPercentage.value)) return true
  if (normaliseNotes(n.notes) !== normaliseNotes(notes.value)) return true

  const server = (n.creditNoteItems ?? [])
    .map(it =>
      lineSignature({
        id: it.id,
        description: it.description,
        kWh: it.quantityInKWh,
        tariffPerKWh: (it.tariffInCents ?? 0) / 100
      })
    )
    .sort()

  const local = lines.value.map(l => lineSignature(l)).sort()
  if (server.length !== local.length) return true
  for (let i = 0; i < server.length; i++) {
    if (server[i] !== local[i]) return true
  }
  return false
})

const hasUniqueDescriptions = computed(() => {
  const seen = new Set<string>()
  for (const l of lines.value) {
    const desc = (l.description ?? '').trim()
    if (!desc) continue
    const key = desc.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
  }
  return true
})

const isEditableStatus = computed(() => statusUi.value === CreditNoteStatus.Draft)
const isStatusApproved = computed(() => statusUi.value === CreditNoteStatus.Approved)
const canDeleteLine = computed(() => isEditableStatus.value && lines.value.length > 1)

const isBusy = computed(
  () =>
    creditNoteLoading.value ||
    isSaving.value ||
    isDeletingLine.value ||
    isApproving.value
)

const canSave = computed(() => {
  if (!isEditableStatus.value) return false
  if (!note.value) return false
  if (!hasUnsavedChanges.value) return false
  if (!lines.value.length) return false
  if (!hasUniqueDescriptions.value) return false
  return lines.value.every(l => (l.description ?? '').trim().length > 0)
})

const canApprove = computed(() => isEditableStatus.value && !hasUnsavedChanges.value)
const totals = computed(() => calcCreditNoteTotals(lines.value, vatPercentage.value))
const totalCents = computed(() => totals.value.totalCents)

const displayTotalCents = computed(() => {
  const n = note.value
  if (!n) return 0
  return hasUnsavedChanges.value ? totalCents.value : n.totalAmountInCents
})

function addLine() {
  if (!isEditableStatus.value)
    return

  lines.value = [
    ...lines.value,
    { description: '', kWh: 0, tariffPerKWh: 0 }
  ]
}

async function removeLine(i: number) {
  if (!isEditableStatus.value)
    return
  
  if (lines.value.length <= 1)
    return
  
  lines.value = lines.value.toSpliced(i, 1)
  addSuccess({ title: 'Line removed (save to apply)' })
}

function updateLine({ index, line }: { index: number; line: Line }) {
  if (!isEditableStatus.value)
    return

  lines.value = lines.value.toSpliced(index, 1, line)
}

async function doSave() {
  if (!canSave.value) return

  await wrap(async () => {
    // Anything that existed on the server but is missing locally is a deletion.
    const currentIds = new Set(lines.value.filter(l => l.id != null).map(l => l.id!))
    const idsToDelete = [...serverItemIds.value].filter(id => !currentIds.has(id))

    const payload: CreditNoteCharge[] = lines.value.map(l => ({
      id: l.id,
      description: l.description,
      kWh: l.kWh,
      tariffPerKWh: l.tariffPerKWh
    }))
    await updateLines({
      charges: payload,
      vatPercentage: vatPercentage.value,
      notes: notes.value
    })
    for (const id of idsToDelete) {
      try {
        await deleteLine(id)
      } catch {
        const failedDeletes: number[] = []

        for (const id of idsToDelete) {
          try {
            await deleteLine(id)
          } catch (err) {
            failedDeletes.push(id)
            console.warn('Failed to delete credit note line item', { id, err })
          }
        }

        if (failedDeletes.length) {
          addError({
            title: 'Saved, but some line deletions failed',
            message: `Could not delete ${failedDeletes.length} line(s). Please refresh and try again.`
          })
        }
      }
    }
    const fresh = await getCreditNoteById(creditNoteId.value)
    hydrateFromNote(fresh)
    isInitialised.value = true

    addSuccess({ title: 'Credit note saved' })
  })
}

async function doApprove() {
  if (!canApprove.value) return

  await wrap(async () => {
    try {
      await approve(creditNoteId.value)
      addSuccess({ title: 'Approved' })
    } catch {
      addError({ title: 'Unable to approve' })
    }
  })
}

async function doApproveAndSend() {
  if (!canApprove.value) return

  await wrap(async () => {
    try {
      await approve(creditNoteId.value)
      await send(creditNoteId.value)
      addSuccess({ title: 'Approved & sent' })
    } catch {
      addError({ title: 'Approve & Send failed' })
    }
  })
}

async function doSend() {
  await wrap(async () => {
    try {
      await send(creditNoteId.value)
      addSuccess({ title: 'Sent' })
    } catch {
      addError({ title: 'Unable to send' })
    }
  })
}

function doCancel() {
  allowLeave.value = true
  router.push({ name: 'invoices.list' })
}

const showError = computed(() => creditNoteIsError.value && !note.value)

const asset = computed(() => assetQuery.data.value)
const customer = computed(() => customerQuery.data.value)
const fund = computed(() => asset.value?.contract?.contractFundEntity)

const pageTitle = computed(() => asset.value?.projectName ?? 'Credit Note')
const pageSubtitle = computed(() => {
  if (!asset.value) return null
  return `(Project: ${asset.value.projectCode} | Asset: ${asset.value.assetCode})`
})
</script>

<template>
  <PageContent>
    <ModalVariantConfirmation
      v-if="showLeaveConfirm"
      title="Discard unsaved changes?"
      rejectText="Stay"
      confirmText="Discard & leave"
      @reject="rejectLeave"
      @confirm="confirmLeave">
      You have unsaved changes. If you leave now, your changes (including deleted line items) will
      be lost.
    </ModalVariantConfirmation>

    <ContentLoader :loading="creditNoteLoading && !note" />
    <HeroSplashError v-if="showError" />

    <div v-else-if="note">
      <div class="mb-4">
        <PageTitle>
          {{ pageTitle }}
          <template v-if="pageSubtitle" #subtitle>{{ pageSubtitle }}</template>
        </PageTitle>

        <div class="mt-2">
          <ButtonSecondary
            :icon="faArrowLeft"
            :class="['one-h-width', $style['back-to-dashboard-btn']]"
            @click="goBackToDashboard">
            Back to Dashboard
          </ButtonSecondary>
        </div>
      </div>

      <CreditNoteSkeleton title="Credit Note">
        <template #credit-note-details>
          <CreditNoteSkeletonDetails
            :credit-note-number="note.creditNoteReference"
            :date="note.date"
            date-format="d MMMM yyyy" />
        </template>

        <template #bill-total>
          <CreditNoteSkeletonBillTotal :amount="displayTotalCents" label="Your Credit" />
        </template>

        <template #owner-details>
          <InvoiceSkeletonOwnerDetails
            :logo-url="fund?.logoUrl ?? null"
            :name="fund?.name ?? ''"
            :address="fund?.address ?? ''"
            :vat-number="fund?.vatNumber ?? ''" />
        </template>

        <template #customer-details>
          <CreditNoteSkeletonCustomerDetails
            :name="customer?.name ?? ''"
            :address="[
              customer?.addressLineOne ?? null,
              customer?.addressLineTwo ?? null,
              customer?.suburb ?? null,
              customer?.city ?? null,
              customer?.region ?? null,
              customer?.country ?? null
            ]"
            :vat-number="customer?.vatNumber ?? null" />
        </template>

        <template #line-items>
          <CreditNoteLines
            :lines="lines"
            :vat-percentage="vatPercentage"
            :can-delete="canDeleteLine"
            :readonly="!isEditableStatus"
            @add="addLine"
            @remove="removeLine"
            @update:line="updateLine" />
        </template>

        <template #notes>
          <Card class="mt-4">
            <CardHeader uppercase>NOTES</CardHeader>
            <CardContent>
              <QuillEditor
                v-model:content="notes"
                :readOnly="!isEditableStatus"
                contentType="html"
                theme="snow"
                placeholder="Please type notes here"
                style="min-height: 120px" />
            </CardContent>
          </Card>
        </template>
      </CreditNoteSkeleton>

      <div class="mt-4 d-flex flex-wrap align-items-center gap-3">
        <ButtonPrimary
          :icon="faFloppyDisk"
          class="color-temp-white"
          :disabled="!canSave || isBusy"
          @click="doSave">
          Save
        </ButtonPrimary>

        <ButtonSecondary :icon="faXmark" class="one-h-width" :disabled="isBusy" @click="doCancel">
          Cancel
        </ButtonSecondary>

        <template v-if="isEditableStatus">
          <div class="vr"></div>

          <ButtonHighlighted :disabled="isBusy || !canApprove" @click="doApprove"
            >Approve</ButtonHighlighted
          >

          <ButtonSecondary
            outline="teal"
            :icon="faHandPointUp"
            :disabled="isBusy || !canApprove"
            @click="doApproveAndSend">
            Approve &amp; Send
          </ButtonSecondary>
        </template>

        <ButtonHighlighted v-else-if="isStatusApproved" :disabled="isBusy" @click="doSend"
          >Send</ButtonHighlighted
        >
      </div>
    </div>
  </PageContent>
</template>

<style module>
.back-to-dashboard-btn {
  min-width: 180px;
}
</style>
